import { audienceIcon, type AudienceIcon } from '@shared/audience-icons';
import { classifyAudienceSegment, type AudienceLabel } from '@shared/audience-labels';
import type { Audience, AudienceSegment, Proposal } from '@shared/types';

export interface AudienceCardViewModel {
  id: string;
  name: string;
  description: string;
  modeLabel: 'INTENT' | 'PROFILE' | 'PROFILE + INTENT';
  /**
   * How the card was built, 0-2 entries ordered [who, intent] — e.g.
   * ['b2b', 'intent']. Derived at read time from the resolved query
   * (src/domain/audience-labels.ts); render each entry as its own chip via
   * AUDIENCE_LABEL_TEXT. Empty until a profile segment has been sized.
   */
  labels: AudienceLabel[];
  countLabel: string | null;
  /**
   * Compact audience size for the persona card's size row ("18.4K"), from
   * `estimatedPeople` in ANY sizing state — the slide never marks a count
   * stale. Null only when no estimate exists; the row is then omitted.
   */
  sizeLabel: string | null;
  /** Size-badge icon: the segment worker's pick, else derived (never empty). */
  icon: AudienceIcon;
  definingSignals: string[];
  imageUrl: string | undefined;
  segment: AudienceSegment;
}

const MODE_LABELS: Record<AudienceSegment['mode'], AudienceCardViewModel['modeLabel']> = {
  intent: 'INTENT',
  profile: 'PROFILE',
  combined: 'PROFILE + INTENT',
};

const COMPACT_PEOPLE = new Intl.NumberFormat('en-US', { notation: 'compact', maximumSignificantDigits: 3 });

/** 18,412 → "18.4K", 184,210 → "184K", 1,234,567 → "1.23M", 940 → "940". */
export function compactPeople(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return COMPACT_PEOPLE.format(value);
}

export function audienceCards(audience: Audience): AudienceCardViewModel[] {
  return audience.segments.slice(0, 4).map((segment, index) => {
    const id = segment.id || `audience-${index + 1}`;
    const name = segment.name || segment.segment || 'Audience segment';
    const description = segment.description || segment.reason || '';
    return {
      id,
      name,
      description,
      modeLabel: MODE_LABELS[segment.mode] ?? 'PROFILE',
      labels: classifyAudienceSegment(segment),
      countLabel:
        segment.sizingState === 'sized' && segment.estimatedPeople
          ? `${segment.estimatedPeople.value.toLocaleString('en-US')} identified`
          : null,
      sizeLabel: compactPeople(segment.estimatedPeople?.value),
      icon: audienceIcon(segment),
      definingSignals: segment.definingSignals?.slice(0, 4) ?? [],
      imageUrl: segment.imageUrl,
      segment: { ...segment, id, name, description },
    };
  });
}

export function audienceStrategyCopy(audience: Audience, rationale?: string): string {
  const authored = rationale?.trim() || audience.description?.trim();
  return authored || 'Reach distinct audience groups with messaging shaped to what defines each one.';
}

export function tacticAudienceCards(
  proposal: Proposal,
  productId: string | null | undefined,
): AudienceCardViewModel[] {
  const all = audienceCards(proposal.audience);
  const ids = productId
    ? proposal.strategy.tactics.find((tactic) => tactic.productId === productId)?.audienceSegmentIds
    : undefined;
  if (!ids?.length) return all.slice(0, 3);
  const order = new Map(ids.map((id, index) => [id, index]));
  return all
    .filter((card) => order.has(card.id))
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

/** Compact default for the manual Pexels picker: cohort plus real market context. */
export function audienceImageQuery(proposal: Proposal, segment: AudienceSegment): string {
  const context =
    proposal.advertiser.industry?.trim() ||
    proposal.businessIntelligence.productsServices[0]?.trim() ||
    proposal.geography.areas[0]?.label?.trim();
  return [segment.name.trim(), context]
    .filter(Boolean)
    .join(' ')
    .split(/\s+/)
    .slice(0, 8)
    .join(' ')
    .slice(0, 100);
}

export function patchAudienceSegment(
  audience: Audience,
  segmentId: string,
  patch: Partial<AudienceSegment>,
): AudienceSegment[] {
  return audience.segments.map((segment, index) => {
    const currentId = segment.id || `audience-${index + 1}`;
    if (currentId !== segmentId) return segment;
    const next = {
      ...segment,
      id: currentId,
      name: segment.name || segment.segment || 'Audience segment',
      description: segment.description || segment.reason || '',
      ...patch,
    };
    if (patch.imageUrl === '') delete next.imageUrl;
    return next;
  });
}
